import {
	ChevronLeft,
	ChevronRight,
	Close as CloseIcon,
	Delete,
	MoreVert,
} from "@mui/icons-material";
import { type FC, useMemo } from "react";

import { DropdownMenu } from "@moment/design-system/DropdownMenu";

import { IconButton } from "~/ui/Button";
import { DialogClose, DialogContent as UiDialogContent } from "~/ui/Dialog";
import { Label } from "~/ui/Label";
import { useNotAutofocusableTabIndex } from "~/utils/focus/useNotAutofocusableTabIndex";

import { type BaseViewHandle } from "../BaseViewHandle";
import { DialogContentCell } from "./DialogContentCell";

export const DialogContent: FC<{
	table: string;
	viewHandle: BaseViewHandle;
}> = ({ table, viewHandle }) => {
	const optionsTabIndex = useNotAutofocusableTabIndex();
	const closeTabIndex = useNotAutofocusableTabIndex();
	const recordCount = viewHandle.data.length;

	const rowIndex = viewHandle.openItemIndex;
	const row = viewHandle.openItem;

	const columns = useMemo(
		() =>
			!row ? [] : row.allColumns.filter((c) => c.header !== row.primaryDisplayColumn?.header),
		[row]
	);

	if (rowIndex === undefined || !row) {
		return null;
	}

	return (
		<UiDialogContent width="lg" hideCloseButton>
			<div className="flex flex-col max-h-[80vh] -m-6 text-sm">
				<div className="flex flex-row items-center gap-2 h-12 px-8 pt-2 pb-2 border-b">
					<div>Row from {table}</div>
					<div className="!absolute right-2 top-2 flex gap-2">
						<DropdownMenu.Root>
							<DropdownMenu.Trigger asChild>
								<IconButton
									tabIndex={optionsTabIndex}
									preset="quaternary"
									size="sm"
									borderRadius="default"
									icon={<MoreVert />}
									label="Row options"
								/>
							</DropdownMenu.Trigger>
							<DropdownMenu.Content>
								<DropdownMenu.Item
									variant="negative"
									onSelect={() => viewHandle.deleteRow(row)}
								>
									<Delete /> Delete Row
								</DropdownMenu.Item>
							</DropdownMenu.Content>
						</DropdownMenu.Root>
						<DialogClose asChild>
							<IconButton
								tabIndex={closeTabIndex}
								preset="quaternary"
								borderRadius="default"
								icon={<CloseIcon />}
								label="Close"
								size="sm"
							/>
						</DialogClose>
					</div>
				</div>
				<div className="flex flex-col flex-grow space-y-6 overflow-y-auto h-full min-h-0 px-8 py-8 [&>*>label]:text-sm">
					{row.primaryDisplayColumn && (
						<div className="flex flex-col space-y-2">
							<Label className="text-secondary">
								{row.primaryDisplayColumn.header}
							</Label>
							<DialogContentCell
								className="text-2xl font-bold"
								cell={row.primaryDisplayColumn}
							/>
						</div>
					)}
					{columns.map((row) => (
						<div key={row.header} className="flex flex-col space-y-2">
							<Label className="text-secondary">{row.header}</Label>
							<div>
								<DialogContentCell cell={row} />
							</div>
						</div>
					))}
				</div>
				{recordCount > 0 && (
					<div className="flex flex-row items-center rounded-b-md gap-2 h-12 px-8 pt-2 pb-2 border-t bg-secondary">
						<IconButton
							className="ml-auto"
							icon={<ChevronLeft />}
							preset="quaternary"
							label="Back"
							size="sm"
							disabled={rowIndex === 0}
							onClick={viewHandle.openRowDialogAtPrevious}
						/>
						<div>
							{rowIndex + 1} of {recordCount ?? 1}
						</div>
						<IconButton
							className="mr-auto"
							icon={<ChevronRight />}
							preset="quaternary"
							label="Next"
							size="sm"
							disabled={rowIndex >= recordCount - 1}
							onClick={viewHandle.openRowDialogAtNext}
						/>
					</div>
				)}
			</div>
		</UiDialogContent>
	);
};
